import type { EventContentObjectDetail } from 'models';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { Icon, type MenuProps } from 'ui';
import { useDeleteEvent } from './useDeleteEvent';
import { useReportEvent } from './useReportEvent';

export const useEventDropdownActions = ({
  onEventDeleted,
  onRequestAction,
  view = 'detail',
}: {
  onEventDeleted?: () => void;
  onRequestAction: (action: 'duplicate' | 'edit') => void;
  view?: 'detail' | 'feed' | 'program';
}) => {
  const { t } = useTranslation();
  const { canDeleteEvent, canDuplicateEvent, canEditEvent, canReportEvent } = usePermissions();

  const { confirm: deleteEvent, contextHolder: contextHolderDelete } = useDeleteEvent({
    onEventDeleted,
  });
  const { confirm: reportEvent, contextHolder: contextHolderReport } = useReportEvent();

  const getActions = useCallback(
    ({ eventContentObject }: { eventContentObject: EventContentObjectDetail }) => {
      const actions: MenuProps['items'] = [];

      if (view !== 'program' && canEditEvent({ contentObject: eventContentObject, view })) {
        actions.push({
          icon: <Icon icon="pencil-line" />,
          key: 'edit',
          label: t('Edit'),
          onClick() {
            onRequestAction('edit');
          },
        });
      }

      if (canReportEvent({ contentObject: eventContentObject })) {
        actions.push({
          icon: <Icon icon="flag-1-line" />,
          key: 'report',
          label: t('Report'),
          onClick() {
            reportEvent({ event: { id: eventContentObject.event.id, name: eventContentObject.event.name } });
          },
        });
      }

      if (view !== 'program' && canDuplicateEvent({ contentObject: eventContentObject })) {
        actions.push({
          icon: <Icon icon="copy-line" />,
          key: 'duplicate',
          label: t('Duplicate'),
          onClick() {
            onRequestAction('duplicate');
          },
        });
      }

      if (view !== 'program' && canDeleteEvent({ contentObject: eventContentObject, view })) {
        actions.push({
          danger: true,
          icon: <Icon icon="delete-2-line" />,
          key: 'delete',
          label: t('Delete'),
          onClick() {
            deleteEvent({ eventContentObject });
          },
        });
      }

      return actions;
    },
    [
      canDeleteEvent,
      canDuplicateEvent,
      canEditEvent,
      canReportEvent,
      deleteEvent,
      onRequestAction,
      reportEvent,
      t,
      view,
    ],
  );

  return useMemo(
    () => ({
      contextHolders: (
        <>
          {contextHolderDelete}
          {contextHolderReport}
        </>
      ),
      getActions,
    }),
    [getActions, contextHolderDelete, contextHolderReport],
  );
};
